/** UCAT workspace scoring: 4 subtests, each scaled 300–900, SJT banded 1–4, total 1200–3600 */

import {
  clearPaperScores,
  loadPaperScores,
  readUserCache,
  savePaperScore,
  writeUserCache,
} from './paperProgressCache';

export const UCAT_SCORES_KEY = 'ucat_paper_scores';
export const UCAT_TARGET_KEY = 'ucat_workspace_target';
export const UCAT_MIN_TOTAL = 1200;
export const UCAT_MAX_TOTAL = 3600;

/** Raw correct/total → scaled 300–900 score, rounded to the nearest 10. */
export function scaledSubtestScore(correct, total) {
  const t = Number(total);
  if (!t || t <= 0) return null;
  const c = Math.max(0, Number(correct) || 0);
  const raw = 300 + (c / t) * 600;
  return Math.round(raw / 10) * 10;
}

/** Raw correct/total → SJT band 1–4 (Band 1 highest). */
export function sjtBand(correct, total) {
  const t = Number(total);
  if (!t || t <= 0) return null;
  const c = Math.max(0, Number(correct) || 0);
  const pct = (c / t) * 100;
  if (pct >= 80) return 1;
  if (pct >= 60) return 2;
  if (pct >= 40) return 3;
  return 4;
}

export function formatUcatTotal(total) {
  if (total == null || !Number.isFinite(Number(total))) return '—';
  return `${Number(total).toLocaleString()}/3600`;
}

export function formatUcatBand(band) {
  if (band == null || !Number.isFinite(Number(band))) return '—';
  return `Band ${Number(band)}`;
}

export function formatPaperRaw(correct, total) {
  if (total == null || total <= 0 || correct == null) return '—';
  return `${correct}/${total}`;
}

export function loadUcatPaperScores(userId) {
  return loadPaperScores(UCAT_SCORES_KEY, userId);
}

export function saveUcatPaperScore(userId, paperId, score = {}) {
  savePaperScore(UCAT_SCORES_KEY, userId, paperId, score);
}

export function clearUcatPaperScores(userId, paperIds = []) {
  clearPaperScores(UCAT_SCORES_KEY, userId, paperIds);
}

/** Forget the user's cached scores for a sitting's papers (a retake). */
export function resetUcatSittingProgress(userId, paperIds = []) {
  clearUcatPaperScores(userId, paperIds);
}

export function loadUcatTarget(userId) {
  const fallback = {
    value: 2850,
    band: 2,
    label: 'University College London (UCL) — Medicine (MBBS)',
    custom: false,
  };
  return readUserCache(UCAT_TARGET_KEY, userId, fallback);
}

export function saveUcatTarget(userId, target) {
  writeUserCache(UCAT_TARGET_KEY, userId, target);
}

/**
 * paperIdsByKey: { verbal_reasoning, decision_making, quantitative_reasoning, situational_judgement }
 * Returns { subtests: { [key]: { correct, total, scaled } }, band, total, mode, status }
 */
export function sittingScoreFromUcatPapers(paperIdsByKey = {}, scoresMap = {}) {
  const keys = [
    'verbal_reasoning',
    'decision_making',
    'quantitative_reasoning',
    'situational_judgement',
  ];

  const subtests = {};
  let gradedCount = 0;
  let mode = null;

  keys.forEach((key) => {
    const paperId = paperIdsByKey?.[key];
    const entry = paperId ? scoresMap[String(paperId)] : null;
    const done = Boolean(entry && entry.total > 0);
    if (done) {
      gradedCount += 1;
      mode = mode || entry.mode || null;
      const scaled = scaledSubtestScore(entry.correct, entry.total);
      subtests[key] = { correct: entry.correct, total: entry.total, scaled };
    } else {
      subtests[key] = { correct: null, total: null, scaled: null };
    }
  });

  const allGraded = gradedCount === keys.length;
  const status = allGraded ? 'done' : gradedCount > 0 ? 'progress' : 'none';

  const total = allGraded
    ? keys.reduce((sum, key) => sum + (subtests[key].scaled || 0), 0)
    : null;

  const sjEntry = subtests.situational_judgement;
  const band = sjEntry?.total ? sjtBand(sjEntry.correct, sjEntry.total) : null;

  return { subtests, band, total, mode, status };
}

/** N-paper (VR/DM/QR/SJT) status derived from DB attempt statuses. */
export function ucatStatusFromDbPapers(paperIds = [], dbPapers = {}) {
  const statuses = paperIds
    .filter(Boolean)
    .map((id) => dbPapers[String(id)]?.status)
    .filter(Boolean);
  if (!statuses.length) return 'none';
  if (statuses.length === paperIds.filter(Boolean).length && statuses.every((s) => s === 'completed')) {
    return 'done';
  }
  if (statuses.some((s) => s === 'in_progress' || s === 'completed')) return 'progress';
  return 'none';
}
